/**
 * MemberInboxPage — the member portal's integrated inbox, mirroring the staff
 * web app's conversation surface. One list of threads across member
 * communications ("comm") and workspace channels ("channel"), with a
 * list → detail flow: pick a thread, read its messages (auto-marked read),
 * reply, and toggle star / flag / unread per thread.
 *
 * All calls go through lib/memberAuth helpers, which send the member token AND
 * the active-org X-CBO-ID header the member-portal endpoints require.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { useMemberTimezone } from '@/hooks/useMemberTimezone';
import { formatDateInTimezone } from '@/utils/dateTime';
import {
  getInboxThreads,
  getInboxThread,
  markInboxThreadUnread,
  setInboxThreadStar,
  setInboxThreadFlag,
  replyToInboxThread,
  isAuthenticated,
  dedupeInboxThreads,
  inboxRedirectKey,
  memberSenderLabel,
  memberReadReceipt,
  type InboxThread,
  type InboxMessage,
  type InboxChannel,
} from '@/lib/memberAuth';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCheck,
  Flag,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  Star,
} from 'lucide-react';
import { callMetaOf, callNotesMetaOf } from '@/services/callsApi';
import { CallMessageCard } from '@/components/messaging/calls/CallMessageCard';
import { callStore, OPEN_CALL_CHANNEL_EVENT } from '@/components/messaging/calls/callStore';
import {
  MemberCallsProvider,
  MemberThreadJoinButton,
  channelIdFromThreadKey,
} from '@/components/messaging/calls/MemberCalls';
// TRUS-691: meeting cards in the member's DM (Join, Add to calendar, RSVP) + ?meeting= links.
import { meetingMetaOf } from '@/services/meetingsApi';
import { MeetingMessageCard } from '@/components/messaging/meetings/MeetingMessageCard';
import { MemberMeetingsProvider, currentMemberId } from '@/components/messaging/meetings/MemberMeetings';
// TRUS-702: per-message channel badge; internal notes are staff-only.
import { MessageChannelBadge } from '@/components/messaging/MessageChannelBadge';
import { isInternalNote, normalizeChannel } from '@/components/messaging/replyChannel';
// TRUS-703: campaign / phone-call / voicemail cards (no voicemail transcripts for members).
import { MemberDmCardView } from '@/components/messaging/MemberDmCardView';
import { memberDmCardOf } from '@/components/messaging/memberDmCards';
// TRUS-719: "Care team is typing…" in the member's DM thread.
import { useTypingIndicator } from '@/components/messaging/typing/useTypingIndicator';
import { TypingIndicator } from '@/components/messaging/typing/TypingIndicator';
import { memberTypingApi } from '@/services/inboxPhase4Api';

/**
 * Backend timestamps are UTC but sometimes omit the trailing 'Z'; append it so
 * `new Date()` parses them as UTC instead of local wall-clock. (See dateTime.ts)
 */
function toEpoch(iso: string): number {
  const s =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(iso) &&
    !iso.endsWith('Z') &&
    !iso.includes('+') &&
    !/[-+]\d{2}:\d{2}$/.test(iso)
      ? iso + 'Z'
      : iso;
  return new Date(s).getTime();
}

/**
 * Short relative label for the thread list. The <7d delta is timezone-agnostic;
 * the older-than-a-week fallback is rendered in the member's timezone.
 */
function relativeTime(iso: string | null, timezone: string): string {
  if (!iso) return '';
  const then = toEpoch(iso);
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDateInTimezone(iso, timezone, 'MMM d');
}

/** Absolute message timestamp, rendered in the member's timezone. */
function fullTime(iso: string | null, timezone: string): string {
  if (!iso) return '';
  if (Number.isNaN(toEpoch(iso))) return '';
  return formatDateInTimezone(iso, timezone, 'MMM d, h:mm a');
}

const THREAD_REFRESH_MS = 15000;

/** What a refresh can change that the member sees: messages and their read state. */
function receiptSignature(messages: InboxMessage[]): string {
  return messages
    .map((m) => `${m.id}:${m.is_read ? 1 : 0}:${m.read_by_count ?? ''}:${m.seen_at ?? ''}`)
    .join('|');
}

/** Subtle per-channel pill styling, matching the inbox's existing chip look. */
const CHANNEL_STYLES: Record<InboxChannel, string> = {
  SMS: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  Email: 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  Portal: 'border-teal-300 bg-teal-50 text-teal-700 dark:border-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  Phone: 'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  Workspace: 'border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-300',
};

function ChannelBadge({ channel }: { channel?: InboxChannel | null }) {
  if (!channel) return null;
  const style = CHANNEL_STYLES[channel] || CHANNEL_STYLES.Workspace;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${style}`}
    >
      {channel}
    </span>
  );
}

export default function MemberInboxPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const timezone = useMemberTimezone();
  const [searchParams, setSearchParams] = useSearchParams();

  const [threads, setThreads] = useState<InboxThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState('');

  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // TRUS-719: typing indicator — member DMs (`channel:` threads) only. Polls
  // every 3 s while the thread is open and visible; pings (≤ every 4 s) while
  // the reply box has text and focus. Hidden for the session on a 404 / 501.
  const [replyFocused, setReplyFocused] = useState(false);
  const typingKey = selectedKey && selectedKey.startsWith('channel:') ? selectedKey : null;
  const typingIndicator = useTypingIndicator({
    surface: 'member',
    threadKey: typingKey,
    enabled: typingKey != null,
    transport: {
      get: () => memberTypingApi.get(typingKey ?? ''),
      ping: () => memberTypingApi.ping(typingKey ?? ''),
    },
  });
  const notifyTyping = typingIndicator.notifyComposer;
  useEffect(() => {
    notifyTyping(replyText, replyFocused);
  }, [replyText, replyFocused, notifyTyping]);

  const loadThreads = useCallback(async () => {
    setError('');
    try {
      const data = await getInboxThreads();
      // TRUS-709: a member DM is the one thread per organization — drop
      // `comm:` threads the server points at a DM that is listed too.
      setThreads(dedupeInboxThreads(data.threads || []));
    } catch (err: any) {
      setError(err.message || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/member/login');
      return;
    }
    loadThreads();
  }, [navigate, loadThreads]);

  // Deep-link support: /member/inbox?thread=comm:123 (used by dashboard actions).
  const initialThread = searchParams.get('thread');
  const openThread = useCallback(
    async (threadKey: string, hops = 0): Promise<void> => {
      setSelectedKey(threadKey);
      setThreadLoading(true);
      setThreadError('');
      setReplyText('');
      try {
        const detail = await getInboxThread(threadKey);
        // TRUS-709: an old `comm:` link (dashboard action, push, bookmark)
        // resolves to the member's DM — follow it (once) and fix the URL.
        const target = inboxRedirectKey(detail.redirect_thread_key, threadKey);
        if (target && hops < 2) {
          setSelectedKey(target);
          setSearchParams({ thread: target }, { replace: true });
          return await openThread(target, hops + 1);
        }
        setMessages(detail.messages || []);
        // Opening a thread auto-marks it read server-side — reflect that locally.
        setThreads((prev) =>
          prev.map((th) => (th.thread_key === threadKey ? { ...th, unread_count: 0 } : th)),
        );
      } catch (err: any) {
        setThreadError(err.message || 'Failed to load conversation');
        setMessages([]);
      } finally {
        setThreadLoading(false);
      }
    },
    [setSearchParams],
  );

  useEffect(() => {
    // Also when the thread param changes while the inbox is open (a clicked
    // push notification routes here in-app, TRUS-668).
    if (initialThread && initialThread !== selectedKey) {
      openThread(initialThread);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialThread]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // TRUS-638: "Delivered" → "Seen" under the member's newest message. While a
  // member DM (`channel:`) is open and the page is visible, re-read it every
  // 15 s so the caption (and any new reply) updates without reopening.
  const selectedKeyRef = useRef<string | null>(null);
  selectedKeyRef.current = selectedKey;
  useEffect(() => {
    if (!selectedKey || !selectedKey.startsWith('channel:') || threadLoading) return;
    const key = selectedKey;
    const id = window.setInterval(async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const detail = await getInboxThread(key);
        if (selectedKeyRef.current !== key) return;
        const next = detail.messages || [];
        setMessages((prev) => (receiptSignature(prev) === receiptSignature(next) ? prev : next));
      } catch {
        /* keep what is shown; the next tick retries */
      }
    }, THREAD_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [selectedKey, threadLoading]);

  const receipt = useMemo(() => memberReadReceipt(messages), [messages]);

  // TRUS-668 v3: joining a call (banner / ?call= link) shows its DM thread
  // when the member can read it; an invite without access opens the call alone.
  useEffect(() => {
    const show = (channelId: number) => {
      const key = `channel:${channelId}`;
      setSearchParams({ thread: key }, { replace: true });
      openThread(key);
    };
    const pending = callStore.takePendingChannel();
    if (pending != null) show(pending);
    const onOpen = (e: Event) => {
      callStore.takePendingChannel();
      const id = Number((e as CustomEvent).detail?.channelId);
      if (Number.isFinite(id) && id > 0) show(id);
    };
    window.addEventListener(OPEN_CALL_CHANNEL_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CALL_CHANNEL_EVENT, onOpen);
  }, [openThread, setSearchParams]);

  const selectThread = (threadKey: string) => {
    if (selectedKey !== threadKey) {
      setSearchParams({ thread: threadKey }, { replace: true });
    }
    openThread(threadKey);
  };

  const backToList = () => {
    setSelectedKey(null);
    setMessages([]);
    setThreadError('');
    setSearchParams({}, { replace: true });
  };

  const selectedThread = useMemo(
    () => threads.find((th) => th.thread_key === selectedKey) || null,
    [threads, selectedKey],
  );

  const unreadTotal = useMemo(
    () => threads.reduce((sum, th) => sum + (th.unread_count || 0), 0),
    [threads],
  );

  const toggleStar = async (th: InboxThread, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const next = !th.is_starred;
    setThreads((prev) =>
      prev.map((x) => (x.thread_key === th.thread_key ? { ...x, is_starred: next } : x)),
    );
    try {
      await setInboxThreadStar(th.thread_key, next);
    } catch {
      // Revert on failure.
      setThreads((prev) =>
        prev.map((x) => (x.thread_key === th.thread_key ? { ...x, is_starred: !next } : x)),
      );
    }
  };

  const toggleFlag = async (th: InboxThread, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const next = !th.is_flagged;
    setThreads((prev) =>
      prev.map((x) => (x.thread_key === th.thread_key ? { ...x, is_flagged: next } : x)),
    );
    try {
      await setInboxThreadFlag(th.thread_key, next);
    } catch {
      setThreads((prev) =>
        prev.map((x) => (x.thread_key === th.thread_key ? { ...x, is_flagged: !next } : x)),
      );
    }
  };

  const markUnread = async (th: InboxThread, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setThreads((prev) =>
      prev.map((x) =>
        x.thread_key === th.thread_key
          ? { ...x, unread_count: x.unread_count > 0 ? x.unread_count : 1 }
          : x,
      ),
    );
    try {
      await markInboxThreadUnread(th.thread_key);
    } catch {
      /* leave optimistic state; a refresh will reconcile */
    }
  };

  const sendReply = async () => {
    const body = replyText.trim();
    if (!body || !selectedKey || sending) return;
    setSending(true);
    setThreadError('');
    try {
      await replyToInboxThread(selectedKey, body);
      setReplyText('');
      typingIndicator.resetThrottle();
      // Refresh the thread to pull the just-sent message + any state changes.
      const detail = await getInboxThread(selectedKey);
      setMessages(detail.messages || []);
      // Refresh the list so previews/order/requires-response update.
      loadThreads();
    } catch (err: any) {
      setThreadError(err.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const pageHeader = (
    <PageHeader
      leading={
        selectedThread ? (
          <Button variant="ghost" size="sm" onClick={backToList} className="lg:hidden">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('inbox.back', 'Back')}
          </Button>
        ) : undefined
      }
      eyebrow={t('inbox.eyebrow', 'Member Portal · Inbox')}
      title={t('inbox.title', 'Inbox')}
      description={
        unreadTotal > 0
          ? t('inbox.unreadCount', '{{count}} unread', { count: unreadTotal })
          : t('inbox.allCaughtUp', 'All caught up')
      }
      actions={
        <Button variant="outline" size="sm" onClick={loadThreads} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('inbox.refresh', 'Refresh')}
        </Button>
      }
    />
  );

  if (loading) {
    return (
      <>
        {pageHeader}
        <PageContainer size="wide">
          <PageState variant="loading" />
        </PageContainer>
      </>
    );
  }

  // TRUS-668: member DMs are channel threads ("channel:<id>"); members can join
  // calls staff start there (banner, header button, call card) — never start one.
  const selectedChannelId = channelIdFromThreadKey(selectedThread?.thread_key);

  return (
    <MemberCallsProvider>
    <MemberMeetingsProvider>
      {pageHeader}
      <PageContainer size="wide">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {threads.length === 0 ? (
          <PageState
            variant="empty"
            title={t('inbox.emptyTitle', 'No messages yet')}
            message={t(
              'inbox.emptyMessage',
              'Messages from your organization will appear here.',
            )}
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_1fr] gap-4">
            {/* Thread list */}
            <div
              className={`rounded-[10px] border border-v2border bg-surface overflow-hidden ${
                selectedThread ? 'hidden lg:block' : ''
              }`}
            >
              <ul className="divide-y divide-v2border max-h-[calc(100vh-220px)] overflow-y-auto">
                {threads.map((th) => {
                  const active = th.thread_key === selectedKey;
                  const unread = th.unread_count > 0;
                  return (
                    <li key={th.thread_key}>
                      <button
                        onClick={() => selectThread(th.thread_key)}
                        className={`w-full text-left px-4 py-3 transition-colors ${
                          active ? 'bg-paper-2' : 'hover:bg-paper-2/60'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span
                            className={`min-w-0 truncate text-[13px] ${
                              unread ? 'font-semibold text-ink' : 'font-medium text-ink-700'
                            }`}
                          >
                            {th.counterparty || th.title}
                          </span>
                          <span className="flex-shrink-0 text-[11px] text-ink-400">
                            {relativeTime(th.updated_at, timezone)}
                          </span>
                        </div>
                        {th.title && th.title !== th.counterparty && (
                          <div
                            className={`mt-0.5 truncate text-[12px] ${
                              unread ? 'font-medium text-ink-700' : 'text-ink-500'
                            }`}
                          >
                            {th.title}
                          </div>
                        )}
                        <div className="mt-0.5 truncate text-[12px] text-ink-500">
                          {th.preview || '—'}
                        </div>
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <ChannelBadge channel={th.channel} />
                          {unread && (
                            <span className="inline-flex items-center rounded-full bg-teal-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                              {th.unread_count}
                            </span>
                          )}
                          {th.requires_response && (
                            <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                              {t('inbox.responseNeeded', 'Response needed')}
                            </span>
                          )}
                          <span className="ml-auto flex items-center gap-0.5">
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => toggleStar(th, e)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleStar(th);
                                }
                              }}
                              title={th.is_starred ? t('inbox.unstar', 'Unstar') : t('inbox.star', 'Star')}
                              className="rounded p-1 text-ink-400 hover:bg-paper hover:text-amber-500"
                            >
                              <Star
                                className={`w-3.5 h-3.5 ${
                                  th.is_starred ? 'fill-amber-400 text-amber-400' : ''
                                }`}
                              />
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => toggleFlag(th, e)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleFlag(th);
                                }
                              }}
                              title={th.is_flagged ? t('inbox.unflag', 'Unflag') : t('inbox.flag', 'Flag')}
                              className="rounded p-1 text-ink-400 hover:bg-paper hover:text-red-500"
                            >
                              <Flag
                                className={`w-3.5 h-3.5 ${
                                  th.is_flagged ? 'fill-red-500 text-red-500' : ''
                                }`}
                              />
                            </span>
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Thread detail */}
            <div
              className={`rounded-[10px] border border-v2border bg-surface flex flex-col min-h-[calc(100vh-220px)] ${
                selectedThread ? '' : 'hidden lg:flex'
              }`}
            >
              {!selectedThread ? (
                <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center text-ink-400">
                  <Mail className="w-8 h-8 mb-2" />
                  <p className="text-[13px]">
                    {t('inbox.selectThread', 'Select a conversation to read and reply.')}
                  </p>
                </div>
              ) : (
                <>
                  {/* Thread header + per-thread controls */}
                  <div className="flex items-center justify-between gap-2 border-b border-v2border px-4 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-ink">
                        {selectedThread.counterparty || selectedThread.title}
                      </div>
                      {selectedThread.title &&
                        selectedThread.title !== selectedThread.counterparty && (
                          <div className="truncate text-[12px] text-ink-500">
                            {selectedThread.title}
                          </div>
                        )}
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      {selectedChannelId != null && (
                        <MemberThreadJoinButton
                          channelId={selectedChannelId}
                          channelName={selectedThread.counterparty || selectedThread.title}
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStar(selectedThread)}
                        title={selectedThread.is_starred ? t('inbox.unstar', 'Unstar') : t('inbox.star', 'Star')}
                      >
                        <Star
                          className={`w-4 h-4 ${
                            selectedThread.is_starred ? 'fill-amber-400 text-amber-400' : ''
                          }`}
                        />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleFlag(selectedThread)}
                        title={selectedThread.is_flagged ? t('inbox.unflag', 'Unflag') : t('inbox.flag', 'Flag')}
                      >
                        <Flag
                          className={`w-4 h-4 ${
                            selectedThread.is_flagged ? 'fill-red-500 text-red-500' : ''
                          }`}
                        />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => markUnread(selectedThread)}
                        title={t('inbox.markUnread', 'Mark unread')}
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
                    {threadError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{threadError}</AlertDescription>
                      </Alert>
                    )}
                    {threadLoading ? (
                      <div className="flex items-center justify-center py-10 text-ink-400">
                        <Loader2 className="w-5 h-5 animate-spin" />
                      </div>
                    ) : messages.length === 0 && !threadError ? (
                      <div className="py-10 text-center text-[13px] text-ink-400">
                        {t('inbox.noMessages', 'No messages in this conversation.')}
                      </div>
                    ) : (
                      messages.map((msg) => {
                        // Call notes are staff-only (TRUS-690): the server never
                        // sends them here; if one ever arrives, don't show it.
                        if (callNotesMetaOf(msg.metadata)) return null;
                        // Internal notes (TRUS-702) are staff-only too.
                        if (isInternalNote(msg)) return null;
                        const outbound = msg.direction === 'outbound';
                        return (
                          <div
                            key={msg.id}
                            className={`flex flex-col ${outbound ? 'items-end' : 'items-start'}`}
                          >
                            <div
                              className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${
                                outbound
                                  ? 'bg-teal-600 text-white'
                                  : 'bg-paper-2 text-ink'
                              }`}
                            >
                              {meetingMetaOf(msg.metadata) ? (
                                <MeetingMessageCard
                                  meta={meetingMetaOf(msg.metadata)!}
                                  currentMemberId={currentMemberId()}
                                  tone={outbound ? 'inverted' : 'default'}
                                />
                              ) : callMetaOf(msg.metadata) && selectedChannelId != null ? (
                                <CallMessageCard
                                  meta={callMetaOf(msg.metadata)!}
                                  channelId={selectedChannelId}
                                  tone={outbound ? 'inverted' : 'default'}
                                />
                              ) : memberDmCardOf(msg.metadata) ? (
                                <MemberDmCardView
                                  metadata={msg.metadata}
                                  body={msg.body}
                                  viewer="member"
                                  tone={outbound ? 'inverted' : 'default'}
                                />
                              ) : (
                                <div className="whitespace-pre-wrap break-words text-[13px]">
                                  {msg.body}
                                </div>
                              )}
                              <div
                                className={`mt-1 text-[10px] ${
                                  outbound ? 'text-white/70' : 'text-ink-400'
                                }`}
                              >
                                <span data-testid="member-message-sender">
                                  {memberSenderLabel(msg, selectedThread.counterparty || selectedThread.title)}
                                </span>{' '}
                                · {fullTime(msg.created_at, timezone)}
                                {normalizeChannel(msg.channel) && (
                                  <MessageChannelBadge channel={msg.channel} className="ml-1.5 align-middle" />
                                )}
                              </div>
                            </div>
                            {receipt && receipt.id === msg.id && (
                              <div
                                data-testid="member-read-receipt"
                                className={`mt-0.5 inline-flex items-center gap-1 px-1 text-[11px] ${
                                  receipt.seen ? 'text-teal-700 dark:text-teal-400' : 'text-ink-400'
                                }`}
                              >
                                {receipt.seen ? (
                                  <CheckCheck className="h-3 w-3" aria-hidden="true" />
                                ) : (
                                  <Check className="h-3 w-3" aria-hidden="true" />
                                )}
                                {receipt.seen
                                  ? receipt.seenAt
                                    ? t('messaging.receipts.seenAt', 'Seen {{time}}', {
                                        time: fullTime(receipt.seenAt, timezone),
                                      })
                                    : t('messaging.receipts.seen', 'Seen')
                                  : t('messaging.receipts.delivered', 'Delivered')}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* TRUS-719: the care team is typing. */}
                  <TypingIndicator entries={typingIndicator.typing} />

                  {/* Reply composer */}
                  <div className="border-t border-v2border p-3">
                    <div className="flex items-end gap-2">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onFocus={() => setReplyFocused(true)}
                        onBlur={() => setReplyFocused(false)}
                        data-testid="member-reply-input"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            sendReply();
                          }
                        }}
                        rows={2}
                        placeholder={t('inbox.replyPlaceholder', 'Write a reply…')}
                        className="flex-1 resize-none rounded-[8px] border border-v2border bg-paper px-3 py-2 text-[13px] text-ink outline-none focus:border-teal-500"
                      />
                      <Button
                        onClick={sendReply}
                        disabled={sending || !replyText.trim()}
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                      >
                        {sending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-1.5" />
                            {t('inbox.send', 'Send')}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </PageContainer>
    </MemberMeetingsProvider>
    </MemberCallsProvider>
  );
}
